import type { SyncProviderSource } from './metricSourceInfo';
import {
  METRIC_VALUE_SOURCE_HEALTH_CONNECT,
  METRIC_VALUE_SOURCE_HEALTHKIT,
  METRIC_VALUE_SOURCE_OURA,
} from './metricSources';

/** Registered OS connector provider slugs (OsConnection — owned by phone-sync-pipeline). */
export const OS_METRIC_CONNECTOR_PROVIDERS = ['healthkit', 'health-connect'] as const;

export type OsMetricConnectorProvider = (typeof OS_METRIC_CONNECTOR_PROVIDERS)[number];

/** Registered cloud connector provider slugs (CloudConnection). */
export const CLOUD_METRIC_CONNECTOR_PROVIDERS = ['oura'] as const;

export type CloudMetricConnectorProvider = (typeof CLOUD_METRIC_CONNECTOR_PROVIDERS)[number];

/** Every OS/cloud provider that must appear in the theme pin list. */
export const METRIC_CONNECTOR_PROVIDER_KEYS: readonly string[] = [
  ...OS_METRIC_CONNECTOR_PROVIDERS,
  ...CLOUD_METRIC_CONNECTOR_PROVIDERS,
];

/**
 * Maps an OsConnection / CloudConnection `connectorKey` to `MetricValue.source`.
 * Adding a connector adds one entry here.
 */
export const CONNECTOR_KEY_TO_SYNC_PROVIDER_SOURCE: Readonly<Record<string, SyncProviderSource>> = {
  healthkit: METRIC_VALUE_SOURCE_HEALTHKIT,
  'health-connect': METRIC_VALUE_SOURCE_HEALTH_CONNECT,
  oura: METRIC_VALUE_SOURCE_OURA,
};

/**
 * Auth scheme a metric connector authenticates with. OS connectors pair with the device the app
 * runs on; cloud connectors run the OAuth2 authorization-code flow.
 */
export type MetricConnectorAuthScheme = 'DEVICE' | 'OAUTH2';

/**
 * Client-safe auth scheme per connector key, mirroring `CloudConnectorDefinition.authScheme` for
 * cloud providers. Adding a connector adds one entry here.
 */
export const CONNECTOR_KEY_TO_AUTH_SCHEME: Readonly<Record<string, MetricConnectorAuthScheme>> = {
  healthkit: 'DEVICE',
  'health-connect': 'DEVICE',
  oura: 'OAUTH2',
};

/**
 * Returns whether a connector key is a registered metric OS/cloud provider.
 * @param connectorKey Pin or root `connectorKey` value.
 * @returns True when the key is a known OS or cloud metric provider.
 */
export function isMetricConnectorProvider(connectorKey: string): boolean {
  return METRIC_CONNECTOR_PROVIDER_KEYS.includes(connectorKey);
}

/**
 * Strips the stable identity suffix from a stored `connectorKey` (`provider#suffix` → `provider`).
 * @param connectorKey Full ConnectionItem connectorKey.
 * @returns Provider or toolkit slug used in pin lists and registries.
 */
export function connectorKeyPinKey(connectorKey: string): string {
  const hashIndex = connectorKey.indexOf('#');
  return hashIndex >= 0 ? connectorKey.slice(0, hashIndex) : connectorKey;
}

/**
 * Maps an OS/cloud connector key to the MetricValue.source used for that provider.
 * @param connectorKey Pin or ConnectionItem `connectorKey` value.
 * @returns The matching sync source, or null when the key is not a metric connector.
 */
export function connectorKeyToSyncProviderSource(connectorKey: string): SyncProviderSource | null {
  return CONNECTOR_KEY_TO_SYNC_PROVIDER_SOURCE[connectorKeyPinKey(connectorKey)] ?? null;
}

/**
 * Maps an OS/cloud connector key to the MetricValue.source used for that provider.
 * @param connectorKey Pin or ConnectionItem `connectorKey` value.
 * @returns The matching source slug, or null when the key is not a metric connector.
 */
export function metricConnectorKeyToValueSource(connectorKey: string): string | null {
  return connectorKeyToSyncProviderSource(connectorKey);
}

/**
 * Lists the auth schemes a metric connector card shows as chips.
 * @param connectorKey Pin or ConnectionItem `connectorKey` value.
 * @returns One scheme for a known connector, empty for an unregistered key.
 */
export function metricConnectorAuthSchemes(
  connectorKey: string,
): readonly MetricConnectorAuthScheme[] {
  const authScheme = CONNECTOR_KEY_TO_AUTH_SCHEME[connectorKey];
  return authScheme ? [authScheme] : [];
}
